import { observer } from "mobx-react";
import moment from "moment";
import React from "react";
import { Link } from "react-router";
import styled from "styled-components";

import StatusPill from "@client/components/ui/StatusPill";
import Style from "@client/utils/Style";
import { Licence } from "@typings/Licence";
import { Tag } from "@typings/Tag";

const Wrapper = styled.div`
  min-width: 200px;
  background: ${Style.palette.grayscale[0]};
  border-radius: ${Style.borderRadius};
`;
const Title = styled.div`
  font-size: 24px;
  font-weight: ${Style.fontWeights.light};
  margin-bottom: 12px;
  text-align: center;
  padding: 8px;
`;
const BodyWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;
const Licences = styled.div`
  flex: 1;
`;
const LicenceWrapper = styled(Link)`
  display: flex;
  text-decoration: none;
  color: ${Style.palette.black};
  padding: 8px;
  &:hover {
    background: ${Style.palette.grayscale[1]};
  }
  cursor: pointer;
  transition: all ${Style.animations.swift};
`;
const LicenceName = styled.div``;
const TopInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-right: 8px;
`;
const TopInfoDate = styled.div`
  ${Style.exactWidth("38px")}
  ${Style.exactHeight("40px")}
  display: flex;
  flex-direction: column;
  border-radius: ${Style.borderRadius};
  overflow: hidden;
`;
const TopInfoDateTop = styled.div`
  width: 100%;
  font-size: 12px;
  height: 16px;
  line-height: 16px;
  background: white;
  color: ${Style.palette.black};
  text-align: center;
`;
const TopInfoDateBottom = styled.div`
  background: white;
  flex-grow: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${Style.palette.primary};
  font-size: 17px;
  font-weight: ${Style.fontWeights.light};
`;
type Props = {
  licences: Licence[];
  tags: Tag[];
  style: React.CSSProperties;
  emptyComponent: React.ReactNode;
};
@observer
class DashboardLicencesSoonToExpire extends React.Component<Props> {
  renderBody() {
    const validLicences = this.props.licences.filter(
      licence =>
        licence.end_date &&
        new Date(licence.end_date).getTime() > new Date().getTime(),
    );

    const sortedLicences = validLicences.sort(
      (a, b) =>
        new Date(a.end_date!).getTime() - new Date(b.end_date!).getTime(),
    );

    let soonestExpiringLicencesPerCustomer = sortedLicences;

    // If we have too many licences, group them by customer and only show the soonest expiring licence for each customer
    if (sortedLicences.length > 10) {
      // Reduce the sorted array to an object where each key is a `customer_id` and the value is the license with the earliest `end_date` for that customer
      const licencesByCustomer = sortedLicences.reduce(
        (acc, licence) => {
          if (
            !acc[licence.customer_id!] ||
            new Date(acc[licence.customer_id!].end_date!).getTime() >
              new Date(licence.end_date!).getTime()
          ) {
            acc[licence.customer_id!] = licence;
          }
          return acc;
        },
        {} as { [customerId: string]: Licence },
      );

      // Convert the object back to an array
      soonestExpiringLicencesPerCustomer = Object.values(
        licencesByCustomer,
      ).slice(0, 10);
    }

    const top10SoonestExpiringLicencesPerCustomer =
      soonestExpiringLicencesPerCustomer;

    return (
      <Licences>
        {top10SoonestExpiringLicencesPerCustomer.map(l => (
          <LicenceWrapper to={`/licences/${l.id}`} key={l.id}>
            <TopInfo>
              <TopInfoDate>
                <TopInfoDateTop>
                  {moment(l.end_date).format("MMM")}
                </TopInfoDateTop>
                <TopInfoDateBottom>
                  {moment(l.end_date).format("DD")}
                </TopInfoDateBottom>
              </TopInfoDate>
            </TopInfo>
            <LicenceName>
              {l.name}{" "}
              <StatusPill
                label={l.tag || "No Tag"}
                backgroundColor={
                  this.props.tags.find(t => t.name === l.tag)?.color
                }
                style={{ marginTop: "4px" }}
              />
            </LicenceName>
          </LicenceWrapper>
        ))}
      </Licences>
    );
  }

  render() {
    return (
      <Wrapper style={this.props.style}>
        <Title>Licences Soon To Expire</Title>
        <BodyWrapper>
          {this.props.licences.length
            ? this.renderBody()
            : this.props.emptyComponent}
        </BodyWrapper>
      </Wrapper>
    );
  }
}

export default DashboardLicencesSoonToExpire;
